import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../environments/environment';
import { ConfigService } from './config.service';
import { jwtDecode } from 'jwt-decode';
import { AppStateService } from './app-state.service';
import { getApiHeaders } from './utils/api-headers.util';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private accessToken = new BehaviorSubject<string | null>(sessionStorage.getItem('accessToken'));
  private refreshToken = new BehaviorSubject<string | null>(sessionStorage.getItem('refreshToken'));
  private refreshTimeout: any = null;
  private tokenExpiryThreshold = 300000; // 5 minutes
  apiUrl: string = environment.apiUrl;
  //update the apiUrl to include the orgTenantId from the app state service
  orgTenantId: string | null;
  constructor(private http: HttpClient, private router: Router, private configService: ConfigService, private appStateService: AppStateService) {
    this.orgTenantId = this.appStateService.getState().orgTenantId;
    // Use tenant subdomain in all environments for proper multi-tenancy
    if (this.orgTenantId) {
      this.apiUrl = this.apiUrl.replace('https://', `https://${this.orgTenantId}.`);
    }
  }

  private getHeaders() {
    return getApiHeaders();
  }

  verifyRecaptcha(token: string) {
    return this.http.post(`${this.apiUrl}/auth/verify-recaptcha`, { token }, { headers: this.getHeaders() });
  }

  isTokenExpired(token: string): boolean {
      const decoded = jwtDecode<any>(token);
      return new Date().getTime() >= decoded.exp * 1000;
  }


  isTokenExpiringSoon(token: string): boolean {
    try {
      const decoded = jwtDecode<any>(token);
      return new Date().getTime() >= (decoded.exp * 1000) - this.tokenExpiryThreshold;
    } catch (error) {
      return false;
    }
  }

  setToken(token: string) {
    const decodedToken: any =  jwtDecode<any>(token);
    this.accessToken.next(token);
    sessionStorage.setItem('accessToken', token);

    const expiresIn = decodedToken.exp * 1000 - Date.now();
    this.refreshTimeout = setTimeout(() => this.refreshAccessToken(), expiresIn - 60000); // Refresh 1 minute before expiration
  }

  setRefreshToken(token: string) {
    this.refreshToken.next(token);
    sessionStorage.setItem('refreshToken', token);
  }

  isAuthenticated(): boolean {
    const token = this.accessToken.getValue();
    return !!token && !this.isTokenExpired(token);
  }

  getAccessToken(): string | null {
    return this.accessToken.getValue();
  }

  clearTokens() {
    this.accessToken.next(null);
    this.refreshToken.next(null);
    sessionStorage.removeItem('idToken');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
  }

  async refreshAccessToken() {
    const refreshToken = this.refreshToken.getValue();
    if (!refreshToken) {
      this.router.navigate(['/login']);
      return;
    }

    if (this.isTokenExpired(refreshToken)) {
      this.clearTokens();
      this.router.navigate(['/login']);
      return;
    }


    try {
      const response: any = await this.http.post(`${this.apiUrl}/auth/refresh_token`, { refresh_token: refreshToken }).toPromise();
      this.setToken(response.access_token);
    } catch (error) {
      this.clearTokens();
      this.router.navigate(['/login']);
    }
  }

  loginAnonymous(patientId: string, idToken: string) {
    const configData = this.configService.getConfigData();
    return this.http.post(`${this.apiUrl}/auth/exchange_anon_token`, {
      patient_id: patientId, id_token: idToken, client_id: configData.client_id, org_id: configData.org_id
    }, { headers: this.getHeaders() });
  }

  loginDeepLink(linkId: string, linkCode: string) {
    const configData = this.configService.getConfigData();
    return this.http.post(`${this.apiUrl}/auth/exchange_deeplink_token`, {
      link_id: linkId, link_code: linkCode, client_id: configData.client_id, org_id: configData.org_id
    },
    { headers: this.getHeaders() });
  }


  login(patientId: string, idToken: string) {
    const configData = this.configService.getConfigData();
    return this.http.post(`${this.apiUrl}/auth/exchange_token`, {
      patient_id: patientId, id_token: idToken, client_id: configData.client_id, org_id: configData.org_id
    }, { headers: this.getHeaders() });
  }

  logout() {
    this.clearTokens();
    
    // Clear forms reminder data
    const patientId = this.appStateService.getState().patientId || sessionStorage.getItem('patientId');
    if (patientId) {
      const pendingFormsKey = `pending_forms_${patientId}`;
      localStorage.removeItem(pendingFormsKey);
    }
    
    this.router.navigate(['/login']);
  }
}
